import { isStripeBillingStrategy, type BillingStrategyQuery } from '@ctct/domain-layer';
import { useStripeCustomer } from '@ctct/state-server-layer';

type BillingStrategyLockOptions = {
  billingStrategy: BillingStrategyQuery;
};

const isStripeCustomerQueryPending = (stripeCustomerQuery: ReturnType<typeof useStripeCustomer>) =>
  Boolean(stripeCustomerQuery.isLoading || stripeCustomerQuery.isPending);

const useMutationLockedAccountState = (
  includeHouseAccount: boolean,
  { billingStrategy }: BillingStrategyLockOptions
) => {
  const isStripeEnabled = isStripeBillingStrategy(billingStrategy);
  const stripeCustomerQuery = useStripeCustomer(isStripeEnabled);
  const isStripeEligibilityPending = includeHouseAccount && billingStrategy.isPending;
  const isPending =
    isStripeEligibilityPending || (isStripeEnabled && isStripeCustomerQueryPending(stripeCustomerQuery));

  if (isPending) {
    return { isLocked: true, isPending };
  }

  const isLocked = includeHouseAccount
    ? Boolean(stripeCustomerQuery.data?.getIsCoreMutationLockedAccount?.())
    : Boolean(stripeCustomerQuery.data?.getIsSingleBillChildAccount?.());

  return {
    isLocked: isStripeEnabled && isLocked,
    isPending,
  };
};

export const useIsSingleBillChildAccountState = (options: BillingStrategyLockOptions) =>
  useMutationLockedAccountState(false, options);

/**
 * Detects if the current Stripe account is a single-bill child account.
 * Use for add-on and checkout-specific mutation locks.
 * Returns true during query loading to lock until proven otherwise.
 */
export const useIsSingleBillChildAccount = (options: BillingStrategyLockOptions) =>
  useIsSingleBillChildAccountState(options).isLocked;

export const useIsCoreMutationLockedAccountState = (options: BillingStrategyLockOptions) =>
  useMutationLockedAccountState(true, options);

/**
 * Detects if the current Stripe account is mutation-locked (single-bill child or house account).
 * Use for broader core plan selection flows (email tier, currency, prepay, etc.).
 * Returns true during query loading to lock until proven otherwise.
 */
export const useIsCoreMutationLockedAccount = (options: BillingStrategyLockOptions) =>
  useIsCoreMutationLockedAccountState(options).isLocked;

export const useIsCoreMutationLockedChildAccount = useIsCoreMutationLockedAccount;
